import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useCart } from "../lib/CartContext";
import { toast } from "sonner";
import { Minus, Plus, ArrowLeft, ChevronDown, Ruler, Droplets, Truck, RefreshCw, Link as LinkIcon } from "lucide-react";
import { isOnSale, salePercentOff, saleCountdown } from "../lib/sale";

const COLOR_HEX = {
  BLACK: "#0a0a0a",
  WHITE: "#fafafa",
  PINK: "#ec4899",
  RED: "#dc2626",
  GREEN: "#16a34a",
};

const Accordion = ({ icon: Icon, title, testid, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-2 border-zinc-800 bg-zinc-900/40" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-900 transition-colors"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-zinc-300" />
          <span className="font-display text-sm text-zinc-100 uppercase tracking-widest">
            {title}
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className="px-4 pb-4 pt-1 text-sm text-zinc-300 leading-relaxed border-t border-zinc-800"
          data-testid={`${testid}-content`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!product || !isOnSale(product) || !product.sale_ends_at) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [product]);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setSize(r.data.sizes?.[0] || null);
        setColor(r.data.colors?.[0] || null);
      })
      .catch(() => setError("Not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleAdd = () => {
    if (!product) return;
    if (!size) { toast.error("Pick a size"); return; }
    if ((product.colors?.length || 0) > 0 && !color && product.category !== "mystery") { toast.error("Pick a color"); return; }
    add(product, size, color, qty);
    toast.success(`${product.name} (${color || ""} ${size}) added to cart`);
  };

  const handleBuyNow = () => {
    handleAdd();
    navigate("/cart");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <NavBar />
        <div className="py-32 text-center font-mono-tech text-zinc-500 uppercase">// Loading ...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <NavBar />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h2 className="font-display text-5xl text-zinc-50 uppercase">404 // Item Not Found</h2>
          <Link to="/shop" className="mt-6 inline-block font-mono-tech text-zinc-200 uppercase text-sm" data-testid="back-to-shop-link">← Back to Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="product-detail">
      <NavBar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/shop" className="font-mono-tech text-xs uppercase text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-2" data-testid="back-link">
          <ArrowLeft className="w-3 h-3" /> Back to inventory
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-6">
          <div
            className="relative bg-zinc-900 overflow-hidden transition-colors"
            style={{ borderWidth: 6, borderStyle: "solid", borderColor: color ? (COLOR_HEX[color] || "#27272a") : "#27272a" }}
            data-testid="product-image-frame"
          >
            <img
              src={product.image_url}
              alt={product.name}
              className={`w-full h-full object-cover transition-all duration-500 ${
                ((product.stock ?? 0) <= 0 || product.sold_out) ? "grayscale opacity-50 blur-[9px] scale-105" : ""
              }`}
            />
            {((product.stock ?? 0) <= 0 || product.sold_out) && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-950/60 pointer-events-none"
                data-testid="pdp-sold-out-stamp"
              >
                <div className="w-[110%] bg-zinc-950/95 border-y-2 border-red-600 py-5 -rotate-[8deg] shadow-[0_25px_70px_-10px_rgba(220,38,38,0.7)] text-center">
                  <div className="font-display text-5xl text-red-500 tracking-[0.35em] uppercase leading-none">Sold Out</div>
                  <div className="font-mono-tech text-[11px] text-zinc-400 tracking-[0.5em] uppercase mt-3">// Restock Unavailable</div>
                </div>
              </div>
            )}
            {color && (
              <div className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-widest px-2 py-1 border-2"
                style={{ backgroundColor: COLOR_HEX[color] || "#27272a", color: color === "WHITE" ? "#0a0a0a" : "#fafafa", borderColor: color === "WHITE" ? "#27272a" : COLOR_HEX[color] }}
                data-testid="viewing-color-badge"
              >
                Viewing // {color}
              </div>
            )}
          </div>

          <div>
            <div className="font-mono-tech text-xs text-zinc-200 uppercase">[ CATEGORY: {product.category} ]</div>
            <h1 className="font-display text-4xl sm:text-6xl text-zinc-50 uppercase leading-none mt-2">{product.name}</h1>
            {(() => {
              const onSale = isOnSale(product);
              const cd = saleCountdown(product, nowMs);
              const pct = salePercentOff(product);
              return (
                <div className="mt-4">
                  {onSale ? (
                    <div className="flex items-baseline flex-wrap gap-x-4 gap-y-1" data-testid="detail-sale-block">
                      <span className="font-mono-tech text-2xl text-red-500">${Number(product.sale_price).toFixed(2)} USD</span>
                      <span className="font-mono-tech text-lg text-zinc-500 line-through">${product.price.toFixed(2)}</span>
                      {pct > 0 && (
                        <span className="bg-red-600 text-zinc-50 font-mono-tech text-xs px-2 py-0.5 uppercase tracking-widest">-{pct}%</span>
                      )}
                    </div>
                  ) : (
                    <div className="font-mono-tech text-2xl text-zinc-200">${product.price.toFixed(2)} USD</div>
                  )}
                  {cd && (
                    <div
                      className="mt-2 inline-block border border-red-600 bg-red-600/10 text-red-400 font-mono-tech text-xs px-3 py-1 uppercase tracking-widest"
                      data-testid="detail-sale-countdown"
                    >
                      SALE ENDS IN {cd}
                    </div>
                  )}
                </div>
              );
            })()}
            <p className="mt-6 text-zinc-300 leading-relaxed">{product.description}</p>

            <div className="mt-8">
              <div className="font-mono-tech text-xs uppercase text-zinc-400 mb-2">Select Size</div>
              <div className="flex flex-wrap gap-2" data-testid="size-options">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`size-${s}`}
                    onClick={() => setSize(s)}
                    className={`font-mono-tech text-sm px-4 py-2 border-2 uppercase ${
                      size === s
                        ? "border-zinc-300 bg-zinc-300 text-zinc-950"
                        : "border-zinc-800 text-zinc-200 hover:border-zinc-600"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            {product.colors && product.colors.length > 0 && product.category !== "mystery" && (
              <div className="mt-6">
                <div className="font-mono-tech text-xs uppercase text-zinc-400 mb-2">
                  Color {color && <span className="text-zinc-200">// {color}</span>}
                </div>
                <div className="flex flex-wrap gap-3" data-testid="color-options">
                  {product.colors.map((c) => {
                    const hex = COLOR_HEX[c] || "#71717a";
                    const isLight = c === "WHITE";
                    return (
                      <button
                        key={c}
                        type="button"
                        title={c}
                        data-testid={`color-${c}`}
                        onClick={() => setColor(c)}
                        className={`w-9 h-9 border-2 flex items-center justify-center ${
                          color === c ? "border-zinc-200" : isLight ? "border-zinc-700" : "border-zinc-800 hover:border-zinc-500"
                        }`}
                        style={{ backgroundColor: hex }}
                      >
                        {color === c && (
                          <span className={`block w-2 h-2 rounded-full ${isLight ? "bg-zinc-900" : "bg-white"}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center gap-4">
              <div className="font-mono-tech text-xs uppercase text-zinc-400">Qty</div>
              <div className="flex items-center border-2 border-zinc-800" data-testid="qty-controls">
                <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-zinc-200 hover:bg-zinc-900" data-testid="qty-decrease"><Minus className="w-3 h-3" /></button>
                <div className="px-4 py-2 font-mono-tech text-sm text-zinc-100 min-w-[40px] text-center" data-testid="qty-value">{qty}</div>
                <button type="button" onClick={() => setQty((q) => q + 1)} className="px-3 py-2 text-zinc-200 hover:bg-zinc-900" data-testid="qty-increase"><Plus className="w-3 h-3" /></button>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {((product.stock ?? 0) <= 0 || product.sold_out) ? (
                <button
                  type="button"
                  disabled
                  data-testid="pdp-sold-out-btn"
                  className="w-full sm:w-auto bg-zinc-900 border-2 border-red-600 text-red-500 px-8 py-3 font-display text-lg tracking-[0.3em] uppercase cursor-not-allowed opacity-90"
                >
                  Sold Out
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleAdd}
                    data-testid="add-to-cart-btn"
                    className="bg-zinc-300 hover:bg-zinc-200 text-zinc-950 px-6 py-3 font-display text-lg tracking-widest uppercase brutal-shadow-orange hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all"
                  >Add to Cart</button>
                  <button
                    type="button"
                    onClick={handleBuyNow}
                    data-testid="buy-now-btn"
                    className="bg-transparent border-2 border-zinc-100 hover:bg-zinc-100 hover:text-zinc-950 text-zinc-100 px-6 py-3 font-display text-lg tracking-widest uppercase transition-colors"
                  >Buy Now</button>
                </>
              )}
            </div>

            <div className="mt-8 border-t-2 border-zinc-800 pt-4 font-mono-tech text-xs text-zinc-500 uppercase space-y-1">
              <div>SKU // {product.id.slice(0, 8)}</div>
              <div>
                STOCK //{" "}
                {((product.stock ?? 0) <= 0 || product.sold_out) ? (
                  <span className="text-red-500">SOLD OUT</span>
                ) : (
                  <>{product.stock} UNITS</>
                )}
              </div>
              <div>SHIPS // 3-5 BUSINESS DAYS</div>
            </div>

            {/* Product info accordions */}
            <div className="mt-6 space-y-3" data-testid="pdp-accordions">
              <Accordion icon={Ruler} title="Size Guide" testid="pdp-size-guide">
                <p className="text-zinc-400 mb-3">
                  Slightly relaxed fit. If between sizes, size down for fitted, up for oversized.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left font-mono-tech text-xs">
                    <thead>
                      <tr className="border-b-2 border-zinc-700 text-zinc-200 uppercase tracking-widest">
                        <th className="py-2 pr-4">Size</th>
                        <th className="py-2 pr-4">Chest (in)</th>
                        <th className="py-2 pr-4">Length (in)</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {[
                        ["S", "36-38", "27"],
                        ["M", "38-40", "28"],
                        ["L", "42-44", "29"],
                        ["XL", "46-48", "30"],
                        ["XXL", "50-52", "31"],
                        ["3XL", "54-56", "32"],
                      ].map((row) => (
                        <tr key={row[0]} className="border-b border-zinc-800">
                          {row.map((v, i) => (
                            <td key={i} className={`py-2 pr-4 ${i === 0 ? "text-zinc-100" : ""}`}>
                              {v}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Link
                  to="/contact#size-guide"
                  className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-200"
                >
                  <LinkIcon className="w-3 h-3" /> Full size guide
                </Link>
              </Accordion>

              <Accordion icon={Droplets} title="Care Instructions" testid="pdp-care">
                <ul className="list-none space-y-1.5">
                  <li>
                    <span className="font-mono-tech text-[10px] uppercase text-zinc-200">// WASH</span> Machine wash cold, inside out.
                  </li>
                  <li>
                    <span className="font-mono-tech text-[10px] uppercase text-zinc-200">// DRY</span> Tumble low or hang dry.
                  </li>
                  <li>
                    <span className="font-mono-tech text-[10px] uppercase text-zinc-200">// PRINT</span> Do not iron directly on print or rhinestones.
                  </li>
                  <li>
                    <span className="font-mono-tech text-[10px] uppercase text-zinc-200">// NO</span> No bleach, no dry cleaning.
                  </li>
                </ul>
              </Accordion>

              <Accordion icon={Truck} title="Shipping" testid="pdp-shipping">
                <ul className="list-none space-y-1.5">
                  <li>Free US shipping on orders over $150 (otherwise $9.99).</li>
                  <li>US delivery 3–5 business days · International 7–14 days.</li>
                  <li>Orders ship within 1–2 business days with tracking.</li>
                </ul>
              </Accordion>

              <Accordion icon={RefreshCw} title="Returns" testid="pdp-returns">
                <ul className="list-none space-y-1.5">
                  <li>30 days for a full refund or exchange.</li>
                  <li>Unworn, unwashed, original tags attached.</li>
                  <li>Halloween Capsule &amp; sale items are exchange-only.</li>
                </ul>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
